import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { format } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import i18n, { TFunction } from 'i18next';
import React, { FC } from 'react';
import { NewOrderType } from 'types/order';
import { toISO8601LocalString } from 'utils/helpers';

import AttendeesSection from './component/attendees-section';
import ItemwithIcon from './component/item-with-icon';
import ScheduleModal from './component/ron-schedule-meeting/ron-schedule-meeting';
import VoidMeetings from './component/void-meetings';
import { ColumnsType, tableRowComponent } from './services';
import { Header } from './component/Header';
import { VerifyAttendeesAlert } from './component/VerifyAttendeesAlert';
import { PartyNotInvolved } from './component/PartyNotInvolved';

const capitalizeWords = (value: string) => value
  .split(' ')
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');

const useStyles = makeStyles((theme: any) => ({
  titleCard: {
    height: theme.spacing(10),
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(3)
  },
  container: { padding: theme.spacing(3) },
  dividerStyle: {
    height: 1,
    width: '100%',
    backgroundColor: theme.accent.main
  },
  paperFlex: { display: 'flex' },
  itemBottom: { marginBottom: theme.spacing(6) },
  text: {
    textTransform: 'none',
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  fontBlack: {
    color: theme.palette.common.black,
    fontWeight: 500
  },
  rescheduleButton: {
    '@media (hover: none) and (pointer: coarse)': { minHeight: theme.spacing(5.5) },
    [theme.breakpoints.down('sm')]: {
      minHeight: theme.spacing(5.5),
      width: '100%'
    }
  },
  paper: {
    marginTop: "1rem"
  }
}));

type RonInviteSccreen = {
  t: TFunction;
  columns: ColumnsType;
  onCopyUrl: (url: string) => void;
  end: Date | null;
  start: Date | null;
  date: Date | null;
  tokens: tableRowComponent[];
  handleOpenScheduleModal: (openModal: boolean) => void;
  scheduleModal: boolean,
  loadingOrder: boolean,
  setTokensRon: React.Dispatch<React.SetStateAction<tableRowComponent[]>>;
  graphOrder: NewOrderType | undefined,
  isUserInvolved: boolean
}

const RonInviteScreenScene: FC<RonInviteSccreen> = ({
  t,
  columns,
  onCopyUrl,
  end,
  start,
  date,
  tokens,
  handleOpenScheduleModal,
  scheduleModal,
  loadingOrder,
  setTokensRon,
  graphOrder,
  isUserInvolved
}) => {
  const classes = useStyles();
  const dateLocale = i18n.language === 'es' ? es : enUS;

  return (
    <>
      <Grid container direction="column" className={classes.container}>
        <Grid item xs={12}>
          <Header />
        </Grid>
        <Grid item xs={12}>
          {loadingOrder && <FontAwesomeIcon spin icon="spinner" />}
          {!loadingOrder && !isUserInvolved && <PartyNotInvolved />}
          {!loadingOrder && isUserInvolved && (
            <Paper className={classes.paper}>
              <Grid container direction="column">
                <Grid item xs={12} className={classes.titleCard}>
                  <Typography variant="h6" className={classes.fontBlack}>{t('ron:details')}</Typography>
                </Grid>
                <Grid item>
                  <Divider orientation="horizontal" className={classes.dividerStyle} />
                </Grid>
                <Grid item container xs={12} className={classes.titleCard}>
                  <VerifyAttendeesAlert />
                  {tokens.length > 0
                    && (
                      <Grid container direction="column">
                        <Grid item className={classes.itemBottom}>
                          <ItemwithIcon icon="calendar-alt" text={t('ron:scheduleDateTimeLabel')} />
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm="auto">
                              <Grid container direction="column">
                                <Typography variant="body1" className={classes.fontBlack}>{t('ron:date')}</Typography>
                                <Typography variant="body2" className={classes.fontBlack}>{date ? capitalizeWords(format(date, 'EEEE MMMM do yyyy', { locale: dateLocale })) : ''}</Typography>
                              </Grid>
                            </Grid>
                            <Grid item xs={12} sm="auto">
                              <Grid container direction="column">
                                <Typography variant="body1" className={classes.fontBlack}>{t('ron:start')}</Typography>
                                <Typography variant="body2" className={classes.fontBlack}>{start ? format(start, 'h:mm aaa', { locale: dateLocale }) : ''}</Typography>
                              </Grid>

                            </Grid>
                            <Grid item xs={12} sm="auto">
                              <Grid container direction="column">
                                <Typography variant="body1" className={classes.fontBlack}>{t('ron:end')}</Typography>
                                <Typography variant="body2" className={classes.fontBlack}>{end ? format(end, 'h:mm aaa', { locale: dateLocale }) : ''}</Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item className={classes.itemBottom} xs={12}>
                          <ItemwithIcon icon="user" text={t('ron:attendees')} />
                          <AttendeesSection
                            t={t}
                            tokens={tokens || []}
                            columns={columns}
                            onCopyUrl={onCopyUrl}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Grid
                            container
                            direction="row"
                            justifyContent="flex-end"
                          >
                            <Button
                              disableElevation
                              className={classes.rescheduleButton}
                              color="secondary"
                              variant="contained"
                              onClick={() => handleOpenScheduleModal(true)}
                            >
                              <Typography variant="body2" className={classes.text}>
                                {t('ron:re-schedule-meeting')}
                              </Typography>
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}
                  {!loadingOrder && tokens.length === 0 && (
                    <Grid container direction="column">
                      <Grid item xs={12}>
                        <VoidMeetings
                          t={t}
                          onClickSchedule={() => handleOpenScheduleModal(true)}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Paper>
          )}
        </Grid>
      </Grid>
      {scheduleModal
        && (
          <ScheduleModal
            open={scheduleModal}
            setTokensRon={setTokensRon}
            onClose={() => handleOpenScheduleModal(false)}
            onCancel={() => handleOpenScheduleModal(false)}
            graphOrder={graphOrder}
            tokenParties={tokens.map(({ email }) => email)}
            tokensInfo={{
              parties: tokens.map((item) => item.party_info!),
              date: date ? toISO8601LocalString(date, false) || '' : '',
              init_time: start ? toISO8601LocalString(start, false) || '' : '',
              end_time: end ? toISO8601LocalString(end, false) || '' : ''
            }}
          />
        )}
    </>
  );
};

export default RonInviteScreenScene;
